import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEvents, getCategories } from '../lib/api';
import EventCard from '../components/EventCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Filter, Ticket, X } from 'lucide-react';

const Events = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadEvents();
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      const searchQuery = searchParams.get('search');
      const category = searchParams.get('category');
      
      if (searchQuery) params.search = searchQuery;
      if (category && category !== 'all') params.category = category;
      
      const res = await getEvents(params);
      setEvents(res.data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSearchParams({});
  };

  const hasActiveFilters = searchParams.get('search') || searchParams.get('category');

  return (
    <div className="min-h-screen pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Eventos</h1>
          <p className="text-muted-foreground">
            Descubre los mejores eventos en Honduras
          </p>
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar eventos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="events-search-input"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button type="submit" data-testid="search-button">
              Buscar
            </Button>

            {hasActiveFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters} data-testid="clear-filters">
                <X className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            )}
          </form>
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6">
            {searchParams.get('search') && (
              <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm">
                Búsqueda: "{searchParams.get('search')}"
              </span>
            )}
            {searchParams.get('category') && (
              <span className="px-3 py-1 rounded-full bg-secondary/20 text-secondary text-sm">
                Categoría: {categories.find(c => c.id === searchParams.get('category'))?.name}
              </span>
            )}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : events.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {events.length} evento{events.length !== 1 ? 's' : ''} encontrado{events.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {events.map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <Ticket className="w-20 h-20 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-2xl font-semibold mb-2">No se encontraron eventos</h3>
            <p className="text-muted-foreground mb-6">
              {hasActiveFilters
                ? 'Intenta con otros filtros de búsqueda'
                : 'Próximamente tendremos nuevos eventos'}
            </p>
            {hasActiveFilters && (
              <Button onClick={clearFilters}>
                Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
